import { useCallback } from 'react';

import { useChatStoreActions } from './useChatStoreActions';

export type ConversationParams = {
	id: string;
	name?: string;
	timeCreated?: string;
	agentId?: string;
};

export type PrefetchConversationOptions = {
	conversation: ConversationParams;
	forceRefresh?: boolean;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

export type PrefetchConversationMessagesOptions = {
	conversationId: string;
	/**
	 * When `true`, suppresses the cold-path `fetchConversation` that
	 * `<ChatHistory>` dispatches on its next mount. Defaults to `false` so
	 * callers that just want a pure cache-warm don't accidentally swallow a
	 * legitimate downstream fetch. Pass `true` when the caller is racing
	 * intentionally ahead of `<ChatHistory>` and needs 1:1 wire/UFO parity
	 * with the cold path (e.g. `<WarmChatFetches>`).
	 */
	skipNextFetch?: boolean;
	/**
	 * Fired only when the prefetch actually kicks off a wire request — not on
	 * the no-op short-circuit when a conversation is already loaded or in
	 * flight. Lets warm callsites (e.g. `<WarmChatFetches>`) gate UFO `start`
	 * spans on real work being done.
	 */
	onStart?: () => void;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

export type ChatStoreApi = {
	prefetchConversation: (options: PrefetchConversationOptions) => void;
	/** Warm-path messages prefetch. */
	prefetchConversationMessages: (options: PrefetchConversationMessagesOptions) => void;
};

export const useChatStoreApi = (): ChatStoreApi => {
	const { warmConversationCache, prefetchConversationMessages: prefetchMessagesAction } =
		useChatStoreActions();

	const prefetchConversation = useCallback(
		(options: PrefetchConversationOptions): void => {
			warmConversationCache({
				conversation: options.conversation,
				forceRefresh: options.forceRefresh,
				onSuccess: options.onSuccess,
				onError: options.onError,
			});
		},
		[warmConversationCache],
	);

	const prefetchConversationMessages = useCallback(
		(options: PrefetchConversationMessagesOptions): void => {
			prefetchMessagesAction({
				conversationId: options.conversationId,
				skipNextFetch: options.skipNextFetch,
				onStart: options.onStart,
				onSuccess: options.onSuccess,
				onError: options.onError,
			});
		},
		[prefetchMessagesAction],
	);

	return {
		prefetchConversation,
		prefetchConversationMessages,
	};
};
